"use client";

import { useEffect, useRef, useState } from "react";
import { ACCENT, GUIDE_LOOK, GuideCarry, MONO, TXT, Rep } from "@/app/home/office/parts";

// One curvy thread from the hero to the final CTA, walked by a small guide agent.
// Between scenes the guide walks a side margin and names the section it is in
// (each [data-guide] line). At every story scene it walks onto the scene's floor,
// stops next to the scene's characters at the scene's `data-guide-spot`, says the
// scene's `data-guide-say` line, and walks out the other side — so the visitor's
// eye stays with one thing. At the agent office the thread leads to the exact point
// where the office's own guide starts, nothing is drawn while the office is pinned,
// and the thread leaves from where that guide ends. It carries the product as the story moves on:
// a buyer question → tracking data → a page → an enquiry.

const END_LINE = "get your free audit";
const FEET = 188; // scene viewBox y where the guide walks (just below the scene floor)
const SCENE_W = 800;

type Pt = [number, number];
type Seg = { p: [Pt, Pt, Pt, Pt]; t0: number; t1: number; kind: "margin" | "walk" | "stay" | "enter" | "office" | "exit" | "land"; scene: number };
type Scene = { say: string; k: number; left: number };
type Geo = { w: number; h: number; segs: Seg[]; scenes: Scene[]; content: number; stops: { y: number; end: number; line: string }[]; officeK: number };

const bez = (p: Seg["p"], u: number): Pt => {
  const a = 1 - u;
  return [0, 1].map((i) => a * a * a * p[0][i] + 3 * a * a * u * p[1][i] + 3 * a * u * u * p[2][i] + u * u * u * p[3][i]) as Pt;
};

// The first part of a cubic, cut at u (de Casteljau).
const cut = (p: Seg["p"], u: number): Seg["p"] => {
  const lerp = (a: Pt, b: Pt): Pt => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
  const ab = lerp(p[0], p[1]);
  const bc = lerp(p[1], p[2]);
  const cd = lerp(p[2], p[3]);
  const abc = lerp(ab, bc);
  const bcd = lerp(bc, cd);
  return [p[0], ab, abc, lerp(abc, bcd)];
};

// SVG path data; the office stretch is skipped (the office draws its own guide there),
// and so is the leap onto the audit button (a line there would cross the CTA heading).
const toD = (segs: { p: Seg["p"]; kind: Seg["kind"] }[]) => {
  let d = "";
  let pen = false;
  for (const { p, kind } of segs) {
    if (kind === "office" || kind === "land") {
      pen = false;
      continue;
    }
    if (!pen) d += `M ${p[0].join(" ")} `;
    d += `C ${p[1].join(" ")}, ${p[2].join(" ")}, ${p[3].join(" ")} `;
    pen = true;
  }
  return d;
};

export default function StoryPath() {
  const ref = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState<Geo | null>(null);
  const [a, setA] = useState(0); // scroll anchor: page y at 55% of the viewport
  const [intro, setIntro] = useState(0); // 0 → 1: the guide flies in once on page load
  const [clock, setClock] = useState(0); // seconds, runs only while the guide hops on the audit button

  // Build the route from where sections and scenes sit on the page.
  useEffect(() => {
    const host = ref.current?.parentElement;
    if (!host) return;
    const build = () => {
      const hr = host.getBoundingClientRect();
      const vh = window.innerHeight;
      const w = host.clientWidth;
      const content = Math.max(0, (w - 1400) / 2) + 56;
      const gutter = Math.max(24, content / 2);
      const sideX = (s: number) => (s === 0 ? gutter : w - gutter);
      const segs: Seg[] = [];
      const scenes: Scene[] = [];
      let side = 0;
      let cur: Pt = [gutter, 150];
      let curT = 150;
      const add = (to: Pt, c1: Pt, c2: Pt, t: number, kind: Seg["kind"], scene = -1) => {
        const t1 = Math.max(t, curT + 1);
        segs.push({ p: [cur, c1, c2, to], t0: curT, t1, kind, scene });
        cur = to;
        curT = t1;
      };
      // a gentle wave down the margin; the guide sits at 55% of the viewport here
      const margin = (y: number, tMax = Infinity) => {
        if (y <= cur[1] + 4) return;
        const x = sideX(side);
        const k = side === 0 ? 1 : -1;
        const dy = y - cur[1];
        add([x, y], [cur[0] + 22 * k, cur[1] + dy / 3], [x - 22 * k, cur[1] + (2 * dy) / 3], Math.min(y, tMax), "margin");
      };

      let officeK = 1;
      for (const el of Array.from(host.querySelectorAll<HTMLElement>("[data-guide-spot], [data-guide-in]"))) {
        if (el.dataset.guideIn) {
          // The office pins while its runway scrolls by. Walk in to where its guide starts,
          // exactly when it pins; come out from where its guide ends, exactly when it unpins.
          const pin = el.firstElementChild as HTMLElement | null;
          const osvg = el.querySelector("svg");
          if (!pin || !osvg) continue;
          const sr = osvg.getBoundingClientRect();
          const k = sr.width / 1200;
          const offY = sr.top - pin.getBoundingClientRect().top;
          const left = sr.left - hr.left;
          const runTop = el.getBoundingClientRect().top - hr.top;
          const slack = el.offsetHeight - pin.offsetHeight;
          const aPin = runTop - parseFloat(getComputedStyle(pin).top) + 0.55 * vh;
          const [ix, iy] = el.dataset.guideIn.split(",").map(Number);
          const [ox, oy] = (el.dataset.guideOut ?? el.dataset.guideIn).split(",").map(Number);
          const start: Pt = [left + ix * k, runTop + offY + iy * k];
          const end: Pt = [left + ox * k, runTop + slack + offY + oy * k];
          officeK = k;
          margin(start[1] - 140, aPin - 0.3 * vh);
          const x0 = cur[0];
          add(start, [x0, start[1] - 30], [x0 + (start[0] - x0) * 0.5, start[1]], aPin, "enter");
          add(end, start, end, aPin + slack, "office");
          side = end[0] < w / 2 ? 0 : 1;
          const x1 = sideX(side);
          add([x1, end[1] + 120], [end[0], end[1] + 60], [x1, end[1] + 60], aPin + slack + 0.25 * vh, "exit");
          continue;
        }
        const svg = el.querySelector("svg");
        if (!svg) continue;
        const r = svg.getBoundingClientRect();
        const k = r.width / SCENE_W;
        const left = r.left - hr.left;
        const floor = r.top - hr.top + FEET * k;
        const top = el.getBoundingClientRect().top - hr.top;
        // scene progress p ↔ scroll: the scene's top moves from 90% to 20% of the viewport
        const tAt = (p: number) => top + (-0.35 + 0.7 * p) * vh;
        const spot = left + Number(el.dataset.guideSpot) * k;
        const entry = side === 0 ? left + 30 * k : left + (SCENE_W - 30) * k;
        const exit = side === 0 ? left + (SCENE_W - 30) * k : left + 30 * k;
        const idx = scenes.push({ say: el.dataset.guideSay ?? "", k, left }) - 1;

        margin(floor - 120, tAt(0.3));
        const x0 = cur[0];
        add([entry, floor], [x0, floor - 40], [x0 + (entry - x0) * 0.4, floor], tAt(0.38), "margin");
        add([spot, floor], [entry + (spot - entry) / 3, floor], [entry + ((spot - entry) * 2) / 3, floor], tAt(0.52), "walk", idx);
        add([spot, floor], [spot, floor], [spot, floor], tAt(0.82), "stay", idx);
        add([exit, floor], [spot + (exit - spot) / 3, floor], [spot + ((exit - spot) * 2) / 3, floor], tAt(0.95), "walk", idx);
        side = 1 - side;
        const x1 = sideX(side);
        add([x1, floor + 110], [exit + (x1 - exit) * 0.6, floor], [x1, floor + 40], floor + 110, "margin");
      }
      // after the last scene, walk into the final call to action and land on the audit button
      const steps = Array.from(host.querySelectorAll<HTMLElement>("[data-story-step]"));
      const endY = steps.length ? steps[steps.length - 1].getBoundingClientRect().bottom - hr.top : host.scrollHeight - 200;
      margin(endY - 40);
      const pad = host.querySelector<HTMLElement>("[data-guide-land]");
      if (pad) {
        const b = pad.getBoundingClientRect();
        const to: Pt = [b.left - hr.left + b.width / 2, b.top - hr.top - 2];
        // land no later than the furthest the page can scroll, so the landing is always reached
        const maxA = document.documentElement.scrollHeight - vh - (hr.top + window.scrollY) + 0.55 * vh;
        add(to, [cur[0], endY + 10], [to[0], to[1] - 160], Math.min(to[1] - 0.1 * vh, maxA - 10), "land");
      } else {
        add([w / 2, endY + 50], [cur[0], endY + 10], [w / 2, endY + 10], endY + 50, "margin");
      }

      const stops = Array.from(host.querySelectorAll<HTMLElement>("[data-guide]")).map((el) => {
        const b = el.getBoundingClientRect();
        return { y: b.top - hr.top, end: b.bottom - hr.top, line: el.dataset.guide ?? "" };
      });
      setGeo({ w, h: host.scrollHeight, segs, scenes, content, stops, officeK });
    };
    build();
    const ro = new ResizeObserver(build);
    ro.observe(host);
    window.addEventListener("resize", build);
    // Sections can move without the page changing size (late images, fonts); rebuild when they do.
    let sig = "";
    const id = window.setInterval(() => {
      const top = host.getBoundingClientRect().top;
      const next = Array.from(host.querySelectorAll<HTMLElement>("[data-guide]"))
        .map((el) => Math.round(el.getBoundingClientRect().top - top))
        .join(",");
      if (next !== sig) {
        sig = next;
        build();
      }
    }, 500);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", build);
      window.clearInterval(id);
    };
  }, []);

  // Follow the scroll; ease toward it so wheel steps stay smooth.
  useEffect(() => {
    const host = ref.current?.parentElement;
    if (!host) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let cur = window.innerHeight * 0.55 - host.getBoundingClientRect().top;
    let raf = 0;
    const step = () => {
      const tg = window.innerHeight * 0.55 - host.getBoundingClientRect().top;
      const d = tg - cur;
      cur = reduce || Math.abs(d) < 0.5 ? tg : cur + d * 0.18;
      setA(cur);
      raf = cur === tg ? 0 : requestAnimationFrame(step);
    };
    const kick = () => {
      if (!raf) raf = requestAnimationFrame(step);
    };
    kick();
    window.addEventListener("scroll", kick, { passive: true });
    window.addEventListener("resize", kick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", kick);
      window.removeEventListener("resize", kick);
    };
  }, []);

  // On load, the guide flies in from the right edge, circles the page and shrinks into place.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.scrollY > 200) {
      setIntro(1);
      return;
    }
    const DELAY = 400;
    const DUR = 5200;
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, Math.max(0, (now - t0 - DELAY) / DUR));
      setIntro(p);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const last = geo?.segs[geo.segs.length - 1];
  const landed = !!last && last.kind === "land" && a >= last.t1;

  // Hop on the audit button for as long as the guide stands there.
  useEffect(() => {
    if (!landed || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const step = (now: number) => {
      setClock(now / 1000);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [landed]);

  if (!geo || !geo.segs.length) return <div ref={ref} aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-20 hidden xl:block" />;

  const { segs, scenes } = geo;
  const ac = Math.min(Math.max(a, segs[0].t0), segs[segs.length - 1].t1);
  const found = segs.findIndex((g) => ac < g.t1);
  const i = found === -1 ? segs.length - 1 : found;
  const seg = segs[i];
  const u = Math.min(1, Math.max(0, (ac - seg.t0) / (seg.t1 - seg.t0)));
  const at = bez(seg.p, u);
  const inScene = seg.scene >= 0 ? scenes[seg.scene] : null;
  const scenesPassed = new Set(segs.slice(0, i).filter((g) => g.scene >= 0).map((g) => g.scene)).size;
  const pastOffice = seg.kind === "exit" || segs.slice(0, i).some((g) => g.kind === "office");
  // what it carries: a question, then data (as the office guide does), then the page, then the enquiry
  const carryStage = !pastOffice ? (scenesPassed < 3 ? 0 : 3) : scenesPassed >= 7 ? 7 : 5;

  // In a margin, the guide names the section it stands in; past the last one it points to the audit.
  const stop = [...geo.stops].reverse().find((g) => g.y <= at[1] + 40);
  const lastStop = geo.stops[geo.stops.length - 1];
  const line = !stop || (lastStop && at[1] > lastStop.end) ? END_LINE : stop.line;
  const isEnd = line === END_LINE || line === lastStop?.line;
  const perLine = Math.floor((geo.content - 16 - 18) / 6.35);
  const fitsMargin = perLine >= 9;
  // grow to the office's scale while walking in, and back while walking out
  const scale = inScene ? inScene.k : seg.kind === "enter" ? 0.85 + (geo.officeK - 0.85) * u : seg.kind === "exit" ? geo.officeK + (0.85 - geo.officeK) * u : 0.85;
  const landScale = seg.kind === "land" ? 0.85 + 0.35 * u : 0;
  const inOffice = seg.kind === "office";
  const flying = intro < 1;
  const quiet = inOffice || seg.kind === "enter" || seg.kind === "exit" || seg.kind === "land" || flying;
  const onPad = seg.kind === "land" && u >= 1;

  // Intro flight: starting big at the right edge, the guide circles the page once and a bit
  // (same look as when it walks, plus a cape), shrinking as it goes, then glides down into its place.
  const ease = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const ORBIT = 0.75; // share of the flight spent circling
  const orbitK = (1 - Math.cos(Math.PI * Math.min(1, intro / ORBIT))) / 2; // gentle speed-up and slow-down
  const glideK = ease(Math.min(1, Math.max(0, (intro - ORBIT) / (1 - ORBIT))));
  const centre: Pt = [geo.w / 2, at[1] - 90];
  const rx = geo.w * 0.36;
  const ry = 190;
  const theta = -2.5 * Math.PI * orbitK; // starts on the right, goes up first, circles once and ends on top
  const orbit: Pt = [centre[0] + rx * Math.cos(theta), centre[1] + ry * Math.sin(theta)];
  const fly: Pt = intro < ORBIT ? orbit : [orbit[0] + (at[0] - orbit[0]) * glideK, orbit[1] + (at[1] - orbit[1]) * glideK];
  const pos: Pt = flying ? fly : onPad ? [at[0], at[1] - Math.abs(Math.sin(clock * Math.PI * 1.6)) * 10] : at;
  // which way it faces: on the orbit it moves right while sin(theta) > 0; the glide goes left
  const facing = intro < ORBIT && Math.sin(theta) > 0 ? 1 : -1;
  const size = flying ? (intro < ORBIT ? 3.2 - 1.6 * orbitK : 1.6 + (scale - 1.6) * glideK) : landScale || scale;
  const flyLine = intro < 0.3 ? "incoming! your guide is here" : intro < ORBIT ? "wheee! hold on to your leads" : "ok, follow me ↓";

  return (
    <div ref={ref} aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-20 hidden xl:block">
      <svg width={geo.w} height={geo.h} className="overflow-visible">
        <path d={toD(segs)} fill="none" stroke={ACCENT} strokeOpacity={0.22} strokeWidth={2} strokeDasharray="3 8" strokeLinecap="round" />
        <path d={toD([...segs.slice(0, i), { p: cut(seg.p, u), kind: seg.kind }])} fill="none" stroke={ACCENT} strokeOpacity={0.65} strokeWidth={2.5} strokeLinecap="round" />
        {!inOffice && (
        <g transform={`translate(${pos[0]} ${pos[1]}) scale(${size}) translate(${-pos[0]} ${-pos[1]})`}>
          {flying && <Cape x={pos[0]} y={pos[1]} t={intro * 40} facing={facing} />}
          <Rep at={pos} l={GUIDE_LOOK} t={flying ? intro * 8 : onPad ? clock : ac / 40} seed={20} walking={!flying && seg.kind !== "stay" && !onPad} stride={ac / 6} carrying />
          <GuideCarry x={pos[0]} y={pos[1]} stage={carryStage} />
        </g>
        )}
        {flying && <MarginNote x={pos[0]} y={pos[1] - 62 * size} text={flyLine} perLine={40} edge={[8, geo.w - 8]} accent />}
        {quiet ? null : inScene ? (
          <UnderNote x={at[0]} y={at[1] + 8} text={inScene.say} edge={[inScene.left + 8, inScene.left + SCENE_W * inScene.k - 8]} />
        ) : (
          fitsMargin && <MarginNote x={at[0]} y={at[1] - 46} text={line} perLine={perLine} edge={at[0] < geo.w / 2 ? [8, geo.content - 8] : [geo.w - geo.content + 8, geo.w - 8]} accent={isEnd} />
        )}
      </svg>
      {!inScene && !quiet && !fitsMargin && (
        <div className={`fixed left-4 bottom-4 rounded-full border px-3 py-1.5 font-mono text-[11px] font-semibold shadow-sm ${isEnd ? "bg-accent border-accent text-on-accent" : "bg-surface-raised border-hairline text-ink"}`}>
          guide · {line}
        </div>
      )}
    </div>
  );
}

// The guide's cape for the intro flight, plus speed lines: both stream out behind it,
// opposite to the way it faces (`facing` 1 = heading right). The guide itself is unchanged.
function Cape({ x, y, t, facing }: { x: number; y: number; t: number; facing: number }) {
  const f = Math.sin(t) * 4;
  const b = -facing; // the back
  return (
    <g>
      <g stroke={TXT} strokeOpacity={0.3} strokeWidth={1.5} strokeLinecap="round">
        <line x1={x + 40 * b} y1={y - 34} x2={x + 56 * b} y2={y - 34} />
        <line x1={x + 44 * b} y1={y - 22} x2={x + 66 * b} y2={y - 22} />
        <line x1={x + 40 * b} y1={y - 10} x2={x + 54 * b} y2={y - 10} />
      </g>
      <path
        d={`M${x - 8 * b} ${y - 25} Q${x + 14 * b} ${y - 26 + f} ${x + 32 * b} ${y - 10 + f} L${x + 25 * b} ${y - 2 - f} Q${x + 8 * b} ${y - 9} ${x + 7 * b} ${y - 25} Z`}
        fill="#DC2626"
        stroke="#991B1B"
        strokeWidth={1}
        strokeLinejoin="round"
      />
    </g>
  );
}

// Inside a scene the guide speaks from below its feet, clear of the scene's own bubbles.
function UnderNote({ x, y, text, edge }: { x: number; y: number; text: string; edge: [number, number] }) {
  const w = text.length * 6.35 + 18;
  const bx = Math.min(Math.max(x - w / 2, edge[0]), edge[1] - w);
  const top = y + 6;
  return (
    <g>
      <rect x={bx} y={top} width={w} height={18} rx={4} fill={ACCENT} />
      <path d={`M${x - 4} ${top + 0.5} L${x} ${top - 5} L${x + 4} ${top + 0.5}`} fill={ACCENT} />
      <text x={bx + w / 2} y={top + 12.5} textAnchor="middle" fontFamily={MONO} fontSize={10.5} fontWeight={600} fill="#FFFFFF">
        {text}
      </text>
    </g>
  );
}

// A speech note that wraps onto short lines so it fits inside a page margin.
function MarginNote({ x, y, text, perLine, edge, accent }: { x: number; y: number; text: string; perLine: number; edge: [number, number]; accent: boolean }) {
  const lines: string[] = [];
  for (const word of text.split(" ")) {
    const cur = lines[lines.length - 1];
    if (cur !== undefined && (cur + " " + word).length <= perLine) lines[lines.length - 1] = cur + " " + word;
    else lines.push(word);
  }
  const w = Math.max(...lines.map((l) => l.length)) * 6.35 + 18;
  const h = lines.length * 14 + 8;
  const bx = Math.min(Math.max(x - w / 2, edge[0]), edge[1] - w);
  const top = y - 6 - h;
  const fill = accent ? ACCENT : "var(--il-panel)";
  const stroke = accent ? ACCENT : "var(--il-line)";
  return (
    <g>
      <rect x={bx} y={top} width={w} height={h} rx={4} fill={fill} stroke={stroke} />
      <path d={`M${x - 4} ${top + h - 0.5} L${x} ${top + h + 5} L${x + 4} ${top + h - 0.5}`} fill={fill} stroke={stroke} />
      <rect x={x - 4.5} y={top + h - 2} width={9} height={2.2} fill={fill} />
      {lines.map((l, n) => (
        <text key={n} x={bx + w / 2} y={top + 15 + n * 14} textAnchor="middle" fontFamily={MONO} fontSize={10.5} fontWeight={600} fill={accent ? "#FFFFFF" : TXT}>
          {l}
        </text>
      ))}
    </g>
  );
}
